import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiGetPage, apiUpdatePage } from "../api";
import BrandLogo from "../components/BrandLogo";
import { LogoOrbitBg } from "../components/LogoOrbitBg";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "../hooks/use-toast";
import { ArrowLeft, Pencil, Check, ExternalLink, CreditCard, LayoutTemplate, MessageCircle, Plus, Trash2, MonitorSmartphone } from "lucide-react";

const ADVISOR_PHONE = "5215567687979";
const ADVISOR_MSG =
  "Hola, me interesa iniciar un proyecto con UAICARD BUSINESS. ¿Me pueden asesorar?";
const advisorHref = `https://wa.me/${ADVISOR_PHONE}?text=${encodeURIComponent(ADVISOR_MSG)}`;

const DEFAULT = {
  id: "muestras",
  title: "Muestras UAICARD BUSINESS",
  subtitle:
    "Presentación de nuestras tarjetas de presentación digitales y portafolios web. Explora ejemplos y descubre lo que podemos crear para tu negocio.",
  samples: [
    {
      id: "s1",
      kind: "card",
      label: "Tarjeta de presentación digital",
      description: "Portada horizontal, contacto directo por WhatsApp y correo, redes sociales y galería.",
      link: "/p/cafe-aurora",
    },
    {
      id: "s2",
      kind: "portfolio",
      label: "Portafolio web",
      description: "Página completa con secciones, carruseles de productos, ubicación y video.",
      link: "/p/cafe-aurora",
    },
  ],
};

const Muestras = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canEdit = searchParams.get("edit") === "1";
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const page = await apiGetPage("muestras");
        setData(page && page.title ? page : DEFAULT);
      } catch (e) {
        setData(DEFAULT);
      }
    })();
  }, []);

  if (!data) {
    return <div className="pf-screen" />;
  }

  const save = async () => {
    try {
      await apiUpdatePage("muestras", data);
      toast({ title: "Muestras guardadas" });
      setEditing(false);
    } catch (e) {
      toast({ title: "Error al guardar" });
    }
  };

  const setSample = (id, patch) =>
    setData((d) => ({
      ...d,
      samples: d.samples.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    }));

  const addPlatform = () => {
    setData((d) => ({
      ...d,
      samples: [...d.samples, { id: `s${Date.now()}`, kind: "platform", label: "Plataforma", description: "", link: "" }],
    }));
    setEditing(true);
  };

  const removeSample = (id) =>
    setData((d) => ({ ...d, samples: d.samples.filter((s) => s.id !== id) }));

  const kindIcon = (k) =>
    k === "card" ? <CreditCard className="h-6 w-6" /> : k === "platform" ? <MonitorSmartphone className="h-6 w-6" /> : <LayoutTemplate className="h-6 w-6" />;

  return (
    <div className="pf-screen relative">
      <div className="pf-fixed-bg">
        <LogoOrbitBg />
      </div>

      <header className="relative z-20 flex items-center justify-between gap-3 border-b border-white/10 bg-[#1e1b4b]/50 px-4 py-3 backdrop-blur">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 hover:text-white" onClick={() => navigate("/")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <BrandLogo className="h-7 w-auto" chip />
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="gap-1.5 border-white/20 bg-transparent px-2.5 text-white hover:bg-white/10 hover:text-white" onClick={addPlatform} data-testid="muestras-add-platform">
              <Plus className="h-4 w-4" /> <span className="hidden min-[400px]:inline">Plataforma</span>
            </Button>
          {editing ? (
            <Button size="sm" className="gap-1.5 bg-blue-600 hover:bg-blue-700" onClick={save}>
              <Check className="h-4 w-4" /> Guardar
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="gap-1.5 border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" /> Editar
            </Button>
          )}
          </div>
        )}
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-5 py-12">
        <div className="text-center">
          {editing ? (
            <Input
              value={data.title}
              onChange={(e) => setData({ ...data, title: e.target.value })}
              className="mx-auto max-w-xl bg-white/10 text-center font-display text-xl font-bold text-white"
            />
          ) : (
            <h1 className="txt-anim-float font-display text-3xl font-bold text-white sm:text-4xl">
              {data.title}
            </h1>
          )}
          <div className="mt-4">
            {editing ? (
              <Textarea
                rows={3}
                value={data.subtitle}
                onChange={(e) => setData({ ...data, subtitle: e.target.value })}
                className="mx-auto max-w-2xl bg-white/10 text-center text-white"
              />
            ) : (
              <p className="txt-anim-fade mx-auto max-w-2xl text-white/70">{data.subtitle}</p>
            )}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {data.samples.filter((s) => editing || s.link).map((s) => (
            <div
              key={s.id}
              className="glass-card rounded-2xl p-6 text-white transition duration-300 hover:-translate-y-1 hover:border-blue-400/40"
            >
              {/* Icono + título en la misma fila (ocupa el espacio a un lado del icono) */}
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-500/25 text-blue-200">
                  {kindIcon(s.kind)}
                </span>
                {editing ? (
                  <Input
                    value={s.label}
                    onChange={(e) => setSample(s.id, { label: e.target.value })}
                    className="flex-1 bg-white/10 text-white"
                    placeholder="Título"
                  />
                ) : null}
                {editing ? (
                  <button onClick={() => removeSample(s.id)} className="rounded-lg p-2 text-red-300 hover:bg-white/10" data-testid={`muestras-remove-${s.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                ) : (
                  <h3 className="txt-anim-float flex-1 font-display text-lg font-bold leading-tight">
                    {s.label}
                  </h3>
                )}
              </div>

              {editing ? (
                <div className="mt-4 space-y-2">
                  <Textarea rows={2} value={s.description} onChange={(e) => setSample(s.id, { description: e.target.value })} className="bg-white/10 text-white" placeholder="Descripción" />
                  <Input value={s.link} onChange={(e) => setSample(s.id, { link: e.target.value })} className="bg-white/10 text-white" placeholder="Enlace (/p/slug o https://...)" />
                </div>
              ) : (
                <>
                  <p className="mt-4 text-sm text-white/65">{s.description}</p>
                  <a
                    href={s.link}
                    target={s.link?.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-500"
                  >
                    Ver muestra <ExternalLink className="h-4 w-4" />
                  </a>
                </>
              )}
            </div>
          ))}
        </div>

        {/* CTA final */}
        <div className="mt-14 flex flex-col items-center text-center">
          <h2 className="txt-anim-float font-display text-2xl font-bold text-white sm:text-3xl">
            ¿Estás listo para comenzar tu proyecto?
          </h2>
          <p className="mt-3 max-w-xl text-white/70">
            Cuéntanos tu idea y creamos tu tarjeta digital o portafolio web a la medida de tu negocio.
          </p>
          <a
            href={advisorHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-anim-glow mt-6 inline-flex items-center gap-2.5 rounded-full bg-green-500 px-7 py-3.5 font-display text-sm font-bold tracking-wide text-white shadow-lg transition hover:bg-green-600"
          >
            <MessageCircle className="h-5 w-5" />
            CONTACTAR A UN ASESOR
          </a>
        </div>
      </main>
    </div>
  );
};

export default Muestras;
